/**
 * FilterFieldsDialog — the "which filters do I want?" form.
 *
 * Lists every field in the FILTER_FIELDS catalog, grouped exactly as the filter
 * panel renders them, and lets the user tick the ones to keep. The selection is
 * per-user and persisted by `useConversationFilterFields`.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Check, RotateCcw, SlidersHorizontal, X } from 'lucide-react'
import { R } from './detail/primitives.js'
import {
  FILTER_FIELDS,
  FILTER_GROUPS,
  type FilterFieldKey,
  type FilterFieldMeta,
} from './filterFields.js'

export interface FilterFieldsDialogProps {
  open: boolean
  /** Currently visible fields. */
  value: FilterFieldKey[]
  /**
   * Fields the host panel cannot render at all (e.g. the inbox drawer has no
   * keyword box). They stay untouched in the saved selection — they're simply
   * not offered here, so saving from one surface can't wipe another's choice.
   */
  omit?: FilterFieldKey[]
  onSave: (next: FilterFieldKey[]) => void
  onResetDefaults: () => void
  onClose: () => void
}

export default function FilterFieldsDialog({
  open,
  value,
  omit,
  onSave,
  onResetDefaults,
  onClose,
}: FilterFieldsDialogProps): JSX.Element | null {
  // Draft state so Cancel really cancels — nothing is applied until Save.
  const [draft, setDraft] = useState<Set<FilterFieldKey>>(() => new Set(value))

  // Re-seed each time the dialog opens so it reflects the saved selection.
  useEffect(() => {
    if (open) setDraft(new Set(value))
  }, [open, value])

  const offered = useMemo(
    () => (omit && omit.length > 0 ? FILTER_FIELDS.filter((f) => !omit.includes(f.key)) : FILTER_FIELDS),
    [omit],
  )

  const grouped = useMemo(() => {
    return FILTER_GROUPS.map((group) => ({
      group,
      fields: offered.filter((f) => f.group === group),
    })).filter((g) => g.fields.length > 0)
  }, [offered])

  if (!open) return null

  const toggle = (key: FilterFieldKey): void => {
    setDraft((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const setGroup = (fields: FilterFieldMeta[], on: boolean): void => {
    setDraft((prev) => {
      const next = new Set(prev)
      for (const f of fields) {
        if (on) next.add(f.key)
        else next.delete(f.key)
      }
      return next
    })
  }

  const selectedCount = offered.filter((f) => draft.has(f.key)).length

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-[#0f172a]/40" onClick={onClose} />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4">
        <div className={`flex max-h-[86vh] w-full max-w-3xl flex-col overflow-hidden ${R} bg-white shadow-2xl`}>
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
                <SlidersHorizontal className="h-4 w-4" />
              </span>
              <div className="leading-tight">
                <h3 className="text-sm font-bold text-[#0f172a]">Filter fields</h3>
                <p className="text-[12px] text-slate-400">
                  Pick the filters to keep on your form — {selectedCount} of {offered.length} selected
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto bg-[#F8FAFC] px-5 py-4">
            <div className="flex flex-col gap-3">
              {grouped.map(({ group, fields }) => {
                const allOn = fields.every((f) => draft.has(f.key))
                return (
                  <section key={group} className={`overflow-hidden ${R} border border-slate-200 bg-white`}>
                    <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{group}</span>
                      <span className="text-[11px] tabular-nums text-slate-300">
                        {fields.filter((f) => draft.has(f.key)).length}/{fields.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => setGroup(fields, !allOn)}
                        className="ml-auto text-[11px] font-semibold text-[#3366FF] transition-opacity hover:opacity-70"
                      >
                        {allOn ? 'Clear all' : 'Select all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 p-3 sm:grid-cols-2 lg:grid-cols-3">
                      {fields.map((f) => {
                        const on = draft.has(f.key)
                        const parent = f.dependsOn
                        const parentHidden = parent !== undefined && on && !draft.has(parent)
                        const parentLabel = parent !== undefined
                          ? FILTER_FIELDS.find((x) => x.key === parent)?.label
                          : undefined
                        return (
                          <button
                            key={f.key}
                            type="button"
                            onClick={() => toggle(f.key)}
                            className={`flex items-start gap-2.5 ${R} border px-3 py-2 text-left transition-colors ${
                              on
                                ? 'border-[#3366FF]/40 bg-blue-50'
                                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            <span
                              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center ${R} border transition-colors ${
                                on ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300 bg-white'
                              }`}
                            >
                              {on && <Check className="h-3 w-3" strokeWidth={3} />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className={`block truncate text-[13px] font-semibold ${on ? 'text-[#0f172a]' : 'text-slate-600'}`}>
                                {f.label}
                              </span>
                              {parentHidden ? (
                                <span className="block text-[11px] text-amber-600">
                                  Needs {parentLabel} to be usable
                                </span>
                              ) : f.hint ? (
                                <span className="block truncate text-[11px] text-slate-400">{f.hint}</span>
                              ) : null}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                )
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3">
            <button
              type="button"
              onClick={() => { onResetDefaults(); onClose() }}
              className={`inline-flex items-center gap-1.5 ${R} px-3 py-2 text-[13px] font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900`}
            >
              <RotateCcw className="h-3.5 w-3.5" /> Restore defaults
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className={`${R} px-4 py-2 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-100`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { onSave([...draft]); onClose() }}
                className={`${R} bg-[#3366FF] px-5 py-2 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-[#2952CC]`}
              >
                Save fields
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
